//#region Asset Imports

import * as _ from 'underscore'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'

//#endregion

//#region IMR Imports

import { IMRComponentInitializedArgs } from '@imr/controls.angularjs/components/base'
import { IMRLoadingContainerOptions } from '@imr/controls.angularjs/components/loading-container'
import { IMRPopupOptions, IIMRPopup } from '@imr/controls.angularjs/components/overlay/popup'

//#endregion

import { ColumnModel } from '../../api/api.models'
import { Enums } from '../../../models/common'

import { GridOptions, GridTypes, GridController } from "../grid"
import { LocalizationHelper, TypeHelper, ReferenceTypeComponent, ConvertHelper, LogHelper } from "../../../helpers"
import { SequentialItemSaver, SequentialItemSaverMode, SequentialItemSaverResult, SequentialItemSaverProgress } from "../../../helpers/sequential-item-saver"

import { IMRBulkOperationOptions, IIMRBulkOperation } from "./bulk-operation.interfaces"
import { GridService } from '../../../services/grid'
import { IMRBaseControlController } from '../base'


export class BulkOperationController<T> extends
    IMRBaseControlController<IIMRBulkOperation<T>, IMRBulkOperationOptions<T>>
    implements ng.IComponentController, IIMRBulkOperation<T> {

    // #region Consts

    public get ERROR_INFO_COLUMN_NAME(): string { return 'errorInfo' }
    public get MAIN_GRID_NAME(): string { return 'bulkOperationMainGridName' }
    public get LOG_PREFIX(): string { return '[SIMAX.Common.BulkOperation]' }
    // #endregion

    // #region Members

    public gridOptions: GridOptions
    public popupOptions: IMRPopupOptions
    public loadingContainerOptions: IMRLoadingContainerOptions
    public options: IMRBulkOperationOptions<T>
    public progressBarOptions: DevExpress.ui.dxProgressBarOptions
    public showLoader: boolean = false

    private closeButton: DevExpress.ui.dxButton
    private startButton: DevExpress.ui.dxButton
    private gridController: GridController<T>
    private gridDataSource: T[]
    private imrPopup: IIMRPopup
    private progressBar: DevExpress.ui.dxProgressBar
    private stopSavingButton: DevExpress.ui.dxButton
    private sequentialItemSaver: SequentialItemSaver<T>

    // #endregion

    // #region Ctor

    constructor(private gridService: GridService) {
        super()
    }

    // #endregion


    //#region Component

    protected initialize(options: IMRBulkOperationOptions<T>): JQueryPromise<void> {
        this.gridDataSource = TypeHelper.clone(this.options.itemsToProcess)
        this.initializeLoadingContainerOptions()

        return $
            .when(
                this.initializeImrPopupOptions(),
                this.initializeGridOptions(),
                this.initializeProgressBarOptions()
            )
    }

    //#endregion

    // #region Progress Bar

    private initializeProgressBarOptions(): JQueryPromise<any> {
        const progressText: string = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Progress) + ': '

        this.progressBarOptions = {
            min: 0,
            max: 100,
            value: 0,
            width: "100%",
            statusFormat: (ratio: number, value: number) => {
                return this.getStatus(progressText, ratio)
            },
            onInitialized: (e: any) => this.initializeProgressBar(e)
        }

        return super.waitForOnInitialized(this.progressBarOptions)
    }

    private initializeProgressBar(e: any): void {
        return this.progressBar = e.component
    }

    private getStatus(progressText: string, ratio: number) {
        return progressText + Math.round(ratio * 100) + '%'
    }

    private calculateProgressBar(value: number): void {
        this.setProgressBarValue((value / this.gridDataSource.length) * 100)
    }

    private setProgressBarValue(value: number): void {
        this.progressBar.option('value', value)
    }

    // #endregion

    // #region Loading Container

    private initializeLoadingContainerOptions(): void {
        this.loadingContainerOptions = {
            isTransparent: true
        }
    }

    // #endregion


    // #region Popup

    private initializeImrPopupOptions(): JQueryPromise<any> {
        this.popupOptions = {
            bottomToolbarOptions: {
                innerOptions: {
                    items: this.getToolbarItems()
                }
            },

            innerOptions: {
                title: this.getPopupTitle(),
                deferRendering: false,
                onHiding: () => this.onHide()
            },

            onInitialized: (e: IMRComponentInitializedArgs<IIMRPopup>) => {
                this.initializePopup(e.component)
            }
        }

        return super.waitForOnInitialized(this.popupOptions)
    }

    private initializePopup(component: IIMRPopup): void {
        this.imrPopup = component
    }

    private getPopupTitle(): string {
        return this.options.popupTitle + ' (' + _.size(this.gridDataSource) + ')'
    }

    private getToolbarItems(): DevExpress.ui.dxToolbarOptions[] {
        return [this.getStopSavingButtonOptions(), this.getStartButtonOptions(), this.getCloseButtonOptions()]
    }

    private getCloseButtonOptions(): DevExpress.ui.dxToolbarItem {
        return {
            location: 'after',
            widget: 'dxButton',
            options: {
                text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Close),
                onClick: (e: any) => {
                    this.onCloseButtonClick()
                },
                onInitialized: (e: any) => {
                    this.initializeCloseButton(e)
                },
            },
        };
    }

    private initializeCloseButton(e: any): void {
        this.closeButton = e.component
    }

    private onCloseButtonClick(): void {
        this.imrPopup.hide()
    }

    private getStartButtonOptions(): DevExpress.ui.dxToolbarItem {
        return {
            location: 'after',
            widget: 'dxButton',
            options: {
                text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.StartProcess),
                onClick: (e: any) => {
                    this.deleteButtonClick()
                },
                onInitialized: (e: any) => {
                    this.initializeDeleteButton(e)
                }
            },
        }
    }

    private initializeDeleteButton(e: any) {
        this.startButton = e.component
    }

    private getStopSavingButtonOptions(): DevExpress.ui.dxToolbarItem {
        return {
            location: 'after',
            widget: 'dxButton',
            options: {
                text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Stop),
                onClick: (e: any) => {
                    this.stopSaving()
                },
                onInitialized: (e: any) => {
                    this.initalizeStopButton(e)
                },
                disabled: true
            },
        }
    }

    private initalizeStopButton(e: any) {
        this.stopSavingButton = e.component
    }

    private onHide(): void {

        if (this.sequentialItemSaver != null && this.sequentialItemSaver.isRunning) {
            this.stopSaving()
        }

        this.gridDataSource = []
    }

    private deleteButtonClick(): void {
        this.setDisabledButtonOption(this.startButton, true)
        this.setDisabledButtonOption(this.closeButton, true)
        this.showLoader = true
        this.startRemoving()
    }

    private setDisabledButtonOption(button: DevExpress.ui.dxButton, disabled: boolean): void {
        if (button != null) {
            button.option('disabled', disabled)
        }
    }

    // #endregion


    // #region SequentialItemSaver

    private startRemoving(): void {
        this.sequentialItemSaver = new SequentialItemSaver({
            itemsToSave: this.gridDataSource,
            mode: SequentialItemSaverMode.ContinueOnError,
            saveDelegate: (obj: T) => this.onSave(obj),
            finishedDelegate: (progress: SequentialItemSaverResult<T>) => this.onFinish(),
            progressDelegate: (progress: SequentialItemSaverProgress<T>) => this.onProgress(progress)
        })
        this.sequentialItemSaver.start()
        this.setDisabledButtonOption(this.stopSavingButton, false)

    }

    private stopSaving() {
        this.sequentialItemSaver.cancel()
        this.setDisabledButtonOption(this.stopSavingButton, true)
        this.setDisabledButtonOption(this.startButton, false)
        this.setDisabledButtonOption(this.closeButton, false)
    }

    private onProgress(progress: SequentialItemSaverProgress<T>): void {
        this.calculateProgressBar(progress.index + 1)

    }

    private onFinish(): void {
        this.onOperationFinished()
        this.setDisabledButtonOption(this.closeButton, false)
        this.setDisabledButtonOption(this.stopSavingButton, true)
        this.showLoader = false
    }

    private onSave(obj: T): JQueryPromise<T> {
        return this.options.operationMethod(obj)
            .then((order: T) => {
                return this.onSaveSucces(order)
            }).fail((error: any) => {
                this.onSaveFailed(obj, error)
            })
    }

    private onSaveSucces(obj: T): T {
        this.updateRow(obj, Enums.ValidationStatus.Correct)
        return obj
    }

    private onSaveFailed(obj: T, error: any): void {
        const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)
        obj[this.ERROR_INFO_COLUMN_NAME] = errorMsg
        this.updateRow(obj, Enums.ValidationStatus.Incorrect)
    }

    private updateRow(obj: T, status: Enums.ValidationStatus): void {
        this.gridController.setItemDisplayState(obj, { validation: status })
        this.gridController.table.refresh()
    }

    // #endregion


    // #region Events

    private onOperationFinished(): void {
        if (this.options != null && _.isFunction(this.options.onOperationFinished)) {
            this.options.onOperationFinished()
        }
    }

    // #endregion

    // #region Grid

    private initializeGridOptions(): JQueryPromise<void> {
        return this.gridService
            .getColumnModelByCategory(
                Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__GRID_PARAMS, ReferenceTypeComponent.getModuleByReferenceType(this.options.referenceType)
            )
            .then((columns: ColumnModel[]) => {
                this.gridOptions = {
                    type: GridTypes.SMALL,
                    name: this.MAIN_GRID_NAME,
                    buttonsOptions: {
                        enabled: false
                    },
                    columnsDictionary: columns,
                    getDataSource: () => this.gridDataSource,
                    onInitialized: (e: any, grid: GridController<T>) => this.initializeGrid(grid),

                    masterDetail: {
                        enabled: true,
                        template: 'errors',
                        autoExpandAll: true,
                    },
                    dataSourceParser: (obj: T) => this.dataSourceParser(obj)
                }

                return super.waitForOnInitialized(this.gridOptions)
            })
    }

    private initializeGrid(grid: GridController<T>): void {
        this.gridController = grid
    }

    private dataSourceParser(obj: T): any {
        return {
            additionalFields: [{
                key: this.ERROR_INFO_COLUMN_NAME,
                value: obj[this.ERROR_INFO_COLUMN_NAME]
            }]
        }
    }

    // #endregion

    // #region IIMRBulkOperation

    public setDataSource(items: T[]): void {
        if (this.gridController != null) {
            this.gridController.setDataSource(items)
        } else {
            LogHelper.error(this.LOG_PREFIX, `No defined grid controller.`)
        }

        this.gridDataSource = items

        if (this.imrPopup != null) {
            this.imrPopup.getInnerPopup().option('title', this.getPopupTitle())
        } else {
            LogHelper.error(this.LOG_PREFIX, `No defined popup.`)
        }

    }

    public showPopup(): void {
        this.setProgressBarValue(0)
        this.gridController.clearItemDisplayStates()
        this.imrPopup.show()
        this.setDisabledButtonOption(this.startButton, false)
    }

    // #endregion
}